// src/components/layout/AppShell.jsx
import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../store/AuthContext';
import { getAlerts } from '../../services/api';
import type { UserRole } from '../../types';

interface NavItem { to: string; icon: string; label: string; roles: UserRole[] | null; badge?: boolean }

const NAV: NavItem[] = [
  { to: '/dashboard', icon: '⬛', label: 'Dashboard',       roles: null },
  { to: '/bags',      icon: '📦', label: 'Bag Management',  roles: null },
  { to: '/operator',  icon: '📡', label: 'Operator Scan',   roles: ['SUPER_ADMIN','GOVT_OFFICER'] },
  { to: '/dealer',    icon: '🏬', label: 'Dealer Panel',    roles: ['SUPER_ADMIN','GOVT_OFFICER'] },
  { to: '/dealers',   icon: '🗂️', label: 'Dealer Management',roles: ['SUPER_ADMIN','GOVT_OFFICER'] },
  { to: '/alerts',    icon: '🚨', label: 'Fraud Alerts',    roles: null, badge: true },
  { to: '/upload',    icon: '⬆️', label: 'Excel Upload',    roles: ['SUPER_ADMIN','GOVT_OFFICER'] },
  { to: '/qr-print',  icon: '🖨️', label: 'QR Print Manager',roles: ['SUPER_ADMIN','GOVT_OFFICER'] },
  { to: '/reports',   icon: '📊', label: 'Reports',         roles: null },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);

  // Live unresolved-fraud-alert count for the nav badge — polls every 20s so a
  // newly flagged 3rd scan shows up on the menu from any page.
  React.useEffect(() => {
    let on = true;
    const load = () => getAlerts({ resolved: false, limit: 1 })
      .then(r => { if (on) setAlertCount(r.data.total || 0); })
      .catch(() => {});
    load();
    const id = setInterval(load, 20000);
    return () => { on = false; clearInterval(id); };
  }, [location.pathname]);

  const initials = user?.name?.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase() || 'U';

  const handleLogout = () => { logout(); navigate('/login'); };

  // Close the mobile drawer whenever the route changes
  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div className="app-shell">
      {/* Mobile dim overlay (only visible when drawer open on small screens) */}
      {open && <div className="sb-overlay" onClick={() => setOpen(false)} />}

      {/* Sidebar */}
      <nav className={`sidebar${open ? ' open' : ''}`}>
        <div className="sb-logo">
          <div className="sb-mark">
            <div className="sb-icon">U</div>
            <div>
              <div className="sb-name">Fertilizer Tracking</div>
              <div className="sb-sub">Gov't Portal</div>
            </div>
          </div>
        </div>

        <div className="sb-sec">Main</div>
        <div className="sb-nav">
          {NAV.filter(n => !n.roles || (!!user && n.roles.includes(user.role))).map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) => `sb-link${isActive ? ' active' : ''}`}
            >
              <span>{n.icon}</span>
              {n.label}
              {n.badge && alertCount > 0 && <span className="sb-badge">{alertCount}</span>}
            </NavLink>
          ))}
        </div>

        <div className="sb-user">
          <div className="sb-ava">{initials}</div>
          <div>
            <div className="sb-uname">{user?.name || 'Admin'}</div>
            <div className="sb-urole">{user?.role?.replace(/_/g,' ')}</div>
          </div>
          <button
            onClick={handleLogout}
            style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.4)', fontSize:'16px' }}
            title="Logout"
          >↗</button>
        </div>
      </nav>

      {/* Page area */}
      <div className="page-body">
        {/* Mobile-only top bar with hamburger */}
        <div className="mobile-topbar">
          <button className="hamburger" onClick={() => setOpen(o => !o)} aria-label="Toggle menu">
            <span></span><span></span><span></span>
          </button>
          <div className="mobile-brand">
            <span className="mb-icon">U</span>
            Fertilizer Tracking
          </div>
        </div>
        <Outlet />
      </div>
    </div>
  );
}
